import { createSlice } from "@reduxjs/toolkit";
import { StoreDispatch } from "../store";
import { immediateToast } from "izitoast-react";
import {
  CreateExpenseRequest,
  createExpense,
  searchExpenses,
  SearchExpensesRequest
} from "src/network/graphql/expenseService";
import { searchUser, SearchUserRequest } from "src/network/graphql/userService";
import { getGroupList, SearchGroupListRequest } from "src/network/graphql/groupService";
import { Users } from "src/page/users/usersSlice";
import { Group } from "src/page/group/groupSlice";
import { EMPTY_STRING } from "src/components/constant/constant";
import { formatFullName } from "src/utils";

export type Expense = {
  organisation_id: string;
  title: string;
  description: string;
  expense_id: string;
  object_id: string;
  object_type: string;
  status: string;
  display_id: string;
  expense_date: string;
  expense_type: string;
  expense_category: string;
  expense_done_by: string;
  created_at: string;
};

const expenseSlice = createSlice({
  name: "expense",
  initialState: {
    loading: false,
    error: null,
    expenses: {
      list: [] as Expense[],
      size: 0
    },
    users: {
      list: [] as Users[],
      total: 0
    },
    groups: {
      list: [] as Group[],
      total: 0
    },
    usersLoading: false,
    groupsLoading: false
  },
  reducers: {
    fetchExpensesStart: (state) => {
      state.loading = true;
    },
    fetchExpensesSuccess: (state, action) => {
      state.loading = false;
      state.expenses = action.payload;
    },
    fetchExpensesFail: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    createExpenseStart: (state) => {
      state.loading = true;
    },
    createExpenseSuccess: (state) => {
      state.loading = false;
    },
    createExpenseFail: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchUsersStart: (state) => {
      state.usersLoading = true;
    },
    fetchUsersSuccess: (state, action) => {
      state.usersLoading = false;
      state.users.list = action.payload?.list ?? [];
      state.users.total = action.payload?.total ?? 0;
    },
    fetchUsersFail: (state, action) => {
      state.usersLoading = false;
      state.users.list = [];
      state.users.total = 0;
      state.error = action.payload;
    },
    fetchGroupsStart: (state) => {
      state.groupsLoading = true;
    },
    fetchGroupsSuccess: (state, action) => {
      state.groupsLoading = false;
      state.groups.list = action.payload?.list ?? [];
      state.groups.total = action.payload?.total ?? 0;
    },
    fetchGroupsFail: (state, action) => {
      state.groupsLoading = false;
      state.groups.list = [];
      state.groups.total = 0;
      state.error = action.payload;
    }
  }
});

const {
  fetchExpensesStart,
  fetchExpensesSuccess,
  fetchExpensesFail,
  createExpenseStart,
  createExpenseSuccess,
  createExpenseFail,
  fetchUsersStart,
  fetchUsersSuccess,
  fetchUsersFail,
  fetchGroupsStart,
  fetchGroupsSuccess,
  fetchGroupsFail
} = expenseSlice.actions;

export const fetchExpensesAction = (data: SearchExpensesRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchExpensesStart());
    try {
      const response = await searchExpenses(data);

      dispatch(
        fetchExpensesSuccess({
          list: response.data.search_expense.items,
          size: response.data.search_expense.total_size
        })
      );
    } catch (error) {
      dispatch(fetchExpensesFail(error));
    }
  };
};

export const createExpenseAction = (data: CreateExpenseRequest, onSuccess: (_expenseId: string) => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(createExpenseStart());
    try {
      const response = await createExpense(data);
      const expenseId = response?.data?.create_expense?.expense_id ?? EMPTY_STRING;

      dispatch(createExpenseSuccess());
      onSuccess(expenseId);
      immediateToast("success", {
        message: "Expense created successfully",
        timeout: 3000,
        position: "topCenter"
      });
    } catch (error) {
      dispatch(createExpenseFail(error));
    }
  };
};

export const fetchUsersAction = (data: SearchUserRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchUsersStart());
    try {
      const response = await searchUser(data);

      const structuredResponse = response.data.search_user.user_listing.map((user: any) => ({
        id: user?.display_id,
        firstName: user?.attributes?.first_name,
        lastName: user?.attributes?.last_name,
        email: user?.email,
        role: user?.current_organisation_role,
        status: user?.is_active,
        userId: user?.user_id,
        createdAt: user?.created_at,
        fullName: formatFullName(user?.attributes?.first_name, user?.attributes?.last_name),
        currentOrganisationRole: user?.current_organisation_role
      }));

      dispatch(
        fetchUsersSuccess({
          list: structuredResponse,
          total: response.data.search_user.total_size
        })
      );
    } catch (error) {
      dispatch(fetchUsersFail(error));
    }
  };
};

export const fetchGroupsAction = (data: SearchGroupListRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchGroupsStart());
    try {
      const response = await getGroupList(data);

      const structuredResponse = response.data.search_group?.items || [];

      dispatch(
        fetchGroupsSuccess({
          list: structuredResponse,
          total: response.data.search_group.total_size
        })
      );
    } catch (error) {
      dispatch(fetchGroupsFail(error));
    }
  };
};

export default expenseSlice.reducer;
